import { Monitor, Moon, Sun } from 'lucide-react';
import { useState } from 'react';
import { useTranslation } from 'react-i18next';

import type { User } from '@/api/types';
import { AsyncSection } from '@/app/components/AsyncSection';
import { SubmitError } from '@/app/components/SubmitError';
import { Alert } from '@/app/components/ui/Alert';
import { Button } from '@/app/components/ui/Button';
import { Card, CardHeader } from '@/app/components/ui/Card';
import { Field, FormActions } from '@/app/components/ui/Field';
import { PageHeader } from '@/app/components/ui/PageHeader';
import { getMyProfile, setStatisticsTimezone, updateMyProfile } from '@/features/profile/api';
import { RegionSelect } from '@/features/profile/RegionSelect';
import { TimezoneSelect } from '@/features/profile/TimezoneSelect';
import { markManualLanguageChoice, SUPPORTED_LANGUAGES } from '@/i18n/languagePreference';
import type { SupportedLanguage } from '@/i18n/languagePreference';
import { cn } from '@/lib/cn';
import { getThemePreference, setTheme } from '@/lib/theme';
import type { ThemePreference } from '@/lib/theme';
import { useAsync } from '@/lib/useAsync';
import { useSubmit } from '@/lib/useSubmit';

const FLAGS: Record<SupportedLanguage, string> = {
  en: '🇬🇧',
  nl: '🇳🇱',
  de: '🇩🇪',
  fr: '🇫🇷',
  es: '🇪🇸',
  el: '🇬🇷',
  it: '🇮🇹',
  pt: '🇵🇹',
  tr: '🇹🇷',
};

/**
 * `/profile/preferences` (§ Profiel): language, region, statistics timezone
 * and theme, moved out of the account dropdown and the Profiel/Gegevens
 * card into one tab. Language is a single setting: picking one switches the
 * interface immediately (`i18n.changeLanguage`, same as the old
 * `LanguageSwitcher`) *and* saves it to the record as
 * `user.preferred_language` — there is no longer a separate saved-preference
 * field on the Profiel tab. Every write here still sends the caller's full
 * profile record, since `PUT /users/{userId}` has no partial-update mode.
 */
export function PreferencesPage() {
  const { t, i18n } = useTranslation();
  const profile = useAsync(() => getMyProfile(), []);
  const [themePreference, setThemePreference] = useState<ThemePreference>(() =>
    getThemePreference(),
  );

  const languageForm = useSubmit(async (lng: SupportedLanguage) => {
    if (!profile.data) return;
    markManualLanguageChoice();
    await i18n.changeLanguage(lng);
    await updateMyProfile({
      nickname: profile.data.nickname ?? null,
      preferred_language: lng,
      region: profile.data.region ?? null,
      gender: profile.data.gender ?? null,
      birthdate: profile.data.birthdate ?? null,
      height: profile.data.height ?? null,
      weight: profile.data.weight ?? null,
    });
  });

  function applyTheme(preference: ThemePreference) {
    setTheme(preference);
    setThemePreference(preference);
  }

  return (
    <section className="flex flex-col gap-6">
      <PageHeader title={t('profile.nav.preferences')} />

      <Card>
        <CardHeader title={t('profile.preferredLanguage')} />
        <AsyncSection state={profile} label={t('profile.preferredLanguage')} skeleton="card">
          {() => (
            <>
              <div
                role="group"
                aria-label={t('profile.preferredLanguage')}
                className="flex flex-col gap-1.5"
              >
                {SUPPORTED_LANGUAGES.map((lng) => {
                  const isActive = i18n.resolvedLanguage === lng;
                  return (
                    <button
                      key={lng}
                      type="button"
                      aria-pressed={isActive}
                      disabled={languageForm.pending}
                      onClick={() => void languageForm.submit(lng)}
                      className={cn(
                        'border-line flex items-center gap-2 rounded-2xl border px-3 py-2 text-sm',
                        'disabled:pointer-events-none disabled:opacity-50',
                        isActive ? 'bg-mint text-ink font-semibold' : 'hover:bg-mint-tint',
                      )}
                    >
                      <span aria-hidden="true" className="text-base leading-none">
                        {FLAGS[lng]}
                      </span>
                      {t(`common.languages.${lng}`)}
                    </button>
                  );
                })}
              </div>
              <SubmitError error={languageForm.error} />
            </>
          )}
        </AsyncSection>
      </Card>

      <Card>
        <CardHeader title={t('profile.regionAndTimezone.title')} />
        <AsyncSection state={profile} label={t('profile.regionAndTimezone.title')} skeleton="card">
          {(user) => <RegionAndTimezoneForm user={user} />}
        </AsyncSection>
      </Card>

      <Card>
        <CardHeader title={t('accountMenu.theme.label')} />
        <div
          role="group"
          aria-label={t('accountMenu.theme.label')}
          className="flex max-w-xs gap-1.5"
        >
          {(
            [
              ['light', Sun, t('accountMenu.theme.light')],
              ['dark', Moon, t('accountMenu.theme.dark')],
              ['system', Monitor, t('accountMenu.theme.system')],
            ] as const
          ).map(([value, Icon, label]) => (
            <button
              key={value}
              type="button"
              aria-pressed={themePreference === value}
              onClick={() => applyTheme(value)}
              className={cn(
                'border-line flex flex-1 flex-col items-center gap-1 rounded-2xl border px-2 py-2 text-xs',
                themePreference === value ? 'bg-mint text-ink font-semibold' : 'hover:bg-mint-tint',
              )}
            >
              <Icon className="size-4" aria-hidden="true" />
              {label}
            </button>
          ))}
        </div>
      </Card>
    </section>
  );
}

/**
 * Region and statistics timezone as one form, saved independently from the
 * language buttons above. There is no endpoint that returns the caller's
 * *current* statistics timezone (only `PUT /statistics/timezone` exists), so
 * this field always starts unset here — the same limitation the old
 * Profiel/Gegevens card had (it passed `timezone={null}` unconditionally).
 */
function RegionAndTimezoneForm({ user }: { user: User }) {
  const { t } = useTranslation();
  const [region, setRegion] = useState(user.region ?? '');
  const [tz, setTz] = useState('');
  const [saved, setSaved] = useState(false);

  const form = useSubmit(async () => {
    setSaved(false);
    await Promise.all([
      updateMyProfile({
        nickname: user.nickname ?? null,
        preferred_language: user.preferred_language ?? null,
        region: region.trim() === '' ? null : region,
        gender: user.gender ?? null,
        birthdate: user.birthdate ?? null,
        height: user.height ?? null,
        weight: user.weight ?? null,
      }),
      setStatisticsTimezone(tz.trim() === '' ? null : tz),
    ]);
    setSaved(true);
  });

  return (
    <form
      className="flex flex-col gap-5"
      onSubmit={(event) => {
        event.preventDefault();
        void form.submit();
      }}
    >
      <div className="grid gap-5 sm:grid-cols-2">
        <Field label={t('profile.fields.region')} hint={t('profile.fields.regionHint')}>
          <RegionSelect value={region} onChange={setRegion} />
        </Field>
        <Field label={t('profile.fields.timezone')} hint={t('profile.fields.timezoneHint')}>
          <TimezoneSelect value={tz} onChange={setTz} />
        </Field>
      </div>

      {saved && !form.pending && !form.error && <Alert tone="success">{t('profile.saved')}</Alert>}
      <SubmitError error={form.error} />

      <FormActions>
        <Button type="submit" disabled={form.pending}>
          {t('profile.save')}
        </Button>
      </FormActions>
    </form>
  );
}
